import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User } from '@/types'
import {
  getToken,
  setToken,
  removeToken,
  getTokenExpiresAt,
  setTokenExpiresAt,
  removeTokenExpiresAt,
  removeRefreshToken,
  getCsrfToken,
  setCsrfToken,
  removeCsrfToken,
  setRefreshToken,
  getStoredUser,
  setStoredUser,
  removeStoredUser,
} from '@/utils/auth'

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(getToken())
  const expiresAt = ref<string | null>(getTokenExpiresAt())
  const csrfToken = ref<string | null>(getCsrfToken())
  const user = ref<User | null>(getStoredUser())

  const isAuthenticated = computed(() => !!token.value)
  const displayName = computed(() => user.value?.name || user.value?.email || '')

  const userRoles = computed(() => user.value?.roles || [])
  const isSpeaker = computed(() => userRoles.value.some(r => r.toLowerCase() === 'speaker'))
  const isAdmin = computed(() => userRoles.value.some(r => r.toLowerCase() === 'admin'))
  const canCreateSession = computed(() => isSpeaker.value || isAdmin.value)

  function setAuth(
    newToken: string,
    newUser: User,
    newExpiresAt?: string,
    newCsrfToken?: string,
    newRefreshToken?: string,
  ) {
    token.value = newToken
    user.value = newUser
    setToken(newToken)
    if (newExpiresAt) {
      expiresAt.value = newExpiresAt
      setTokenExpiresAt(newExpiresAt)
    } else {
      expiresAt.value = null
      removeTokenExpiresAt()
    }
    if (newCsrfToken) {
      csrfToken.value = newCsrfToken
      setCsrfToken(newCsrfToken)
    } else {
      csrfToken.value = null
      removeCsrfToken()
    }
    if (newRefreshToken) {
      setRefreshToken(newRefreshToken)
    }
    setStoredUser(newUser)
  }

  function setSessionTokens(
    newToken: string,
    newExpiresAt?: string,
    newCsrfToken?: string,
    newRefreshToken?: string,
  ) {
    token.value = newToken
    setToken(newToken)
    if (newExpiresAt) {
      expiresAt.value = newExpiresAt
      setTokenExpiresAt(newExpiresAt)
    }
    if (newCsrfToken) {
      csrfToken.value = newCsrfToken
      setCsrfToken(newCsrfToken)
    }
    if (newRefreshToken) {
      setRefreshToken(newRefreshToken)
    }
  }

  function setUser(newUser: User) {
    user.value = newUser
    setStoredUser(newUser)
  }

  function logout() {
    token.value = null
    expiresAt.value = null
    csrfToken.value = null
    user.value = null
    removeToken()
    removeRefreshToken()
    removeTokenExpiresAt()
    removeCsrfToken()
    removeStoredUser()
  }

  function initializeAuth() {
    const savedToken = getToken()
    const savedExpiresAt = getTokenExpiresAt()
    const savedCsrfToken = getCsrfToken()
    const savedUser = getStoredUser()
    if (savedToken) {
      token.value = savedToken
      if (savedExpiresAt) expiresAt.value = savedExpiresAt
      if (savedCsrfToken) csrfToken.value = savedCsrfToken
      if (savedUser) user.value = savedUser
    } else {
      expiresAt.value = null
      csrfToken.value = null
      user.value = null
    }
  }

  return {
    token,
    expiresAt,
    csrfToken,
    user,
    isAuthenticated,
    displayName,
    userRoles,
    isSpeaker,
    isAdmin,
    canCreateSession,
    setAuth,
    setSessionTokens,
    setUser,
    logout,
    initializeAuth,
  }
})
